/**
 * @file route-product-report.tsx
 * @module app/modules/finance-reports/route-product-report
 * @description Produces route-product reports with filter controls, paginated records, and Excel export.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.1.1
 * @version 2.1.2
 */

import {
  Button,
  Card,
  Col,
  FormLabel,
  Row,
  Modal,
  Spinner,
} from "react-bootstrap";
import { useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import {
  AllModules,
  OrdersDelivery,
  PAGE_LIMIT,
  RouteProductReports,
  View,
} from "../../../utils/constants";
import APICallService from "../../../api/apiCallService";
import { driver, routePlanning, seller } from "../../../api/apiEndPoints";
import Method from "../../../utils/methods";
import Loader from "../../../Global/loader";
import CustomDatePicker from "../../custom/DateRange/DatePicker";
import { useAuth } from "../auth";
import PermissionModal from "../../modals/permission";
import { useWarehouse } from "../../context/WarehouseContext";
import * as XLSX from "xlsx";
import { CustomSelect } from "../../custom/Select/CustomSelect";

const ExcelExportModal = ({
  show,
  onHide,
  startDate,
  endDate,
}: {
  show: boolean;
  onHide: () => void;
  startDate: any;
  endDate: any;
}) => {
  const { selectedWarehouse } = useWarehouse();
  const [isExporting, setIsExporting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentStep, setCurrentStep] = useState("");
  const [totalRecords, setTotalRecords] = useState(0);
  const [processedRecords, setProcessedRecords] = useState(0);
  const [error, setError] = useState("");

  const handleExport = async () => {
    setIsExporting(true);
    setError("");
    setProgress(0);
    setCurrentStep("Initializing export...");

    try {
      setCurrentStep("Fetching route plans...");

      let params: any = {
        fromDate: startDate
          ? Method.convertDateToFormat(startDate, "YYYY-MM-DD")
          : "",
        toDate: endDate
          ? Method.convertDateToFormat(endDate, "YYYY-MM-DD")
          : "",
        needCount: true,
      };
      if (selectedWarehouse?.value) {
        params.warehouse = selectedWarehouse.value;
      }

      let apiService = new APICallService(
        routePlanning.listOutForDeliveryPlans,
        params,
        "",
        "",
        "",
        "",
        RouteProductReports,
        selectedWarehouse?.value || null
      );
      let response = await apiService.callAPI();

      if (!response || !response.records) {
        setError("Failed to fetch route plans");
        return;
      }

      const routePlans = response.records;
      const total = routePlans.length;
      setTotalRecords(total);

      if (total === 0) {
        setError("No route plans found to export");
        return;
      }

      // Prepare Excel workbook
      const workbook = XLSX.utils.book_new();

      // Process each route plan
      let processedCount = 0;

      for (const plan of routePlans) {
        setCurrentStep(
          `Processing route plan ${processedCount + 1} of ${total}...`
        );

        const worksheetData = [];

        // Add route plan summary headers
        const planHeaders = ["Plan Date", "Driver", "Vehicle"];
        worksheetData.push(planHeaders);

        // Add route plan summary row
        const planSummaryRow = [
          Method.convertDateToDDMMYYYY(plan.planDate),
          plan.driver?.name,
          `${plan.vehicle.name} - ${plan.vehicle.number}`,
        ];
        worksheetData.push(planSummaryRow);

        // Add empty rows for separation
        worksheetData.push(["", "", ""]);
        worksheetData.push(["", "", ""]);

        // Fetch product details for this route
        try {
          setCurrentStep(
            `Fetching products for route ${plan.driver?.name || "route"}...`
          );

          let productParams: any = {
            fromDate: startDate
              ? Method.convertDateToFormat(startDate, "YYYY-MM-DD")
              : "",
            toDate: endDate
              ? Method.convertDateToFormat(endDate, "YYYY-MM-DD")
              : "",
            route: plan.route,
            utcOffset: new Date().getTimezoneOffset(),
          };
          if (selectedWarehouse?.value) {
            productParams.warehouse = selectedWarehouse.value;
          }

          let productApiService = new APICallService(
            seller.routeProductReport,
            productParams,
            "",
            "",
            "",
            "",
            RouteProductReports,
            selectedWarehouse?.value || null
          );
          let productResponse = await productApiService.callAPI();

          if (productResponse && productResponse.length > 0) {
            // Add product headers
            const productHeaders = [
              "Product Name",
              "Total Stock",
              "Returned Stock",
            ];
            worksheetData.push(productHeaders);
            worksheetData.push(["", "", ""]); // Empty row for separation

            // Add product details
            productResponse.forEach((product: any) => {
              const productRow = [
                product.title,
                product.stockCount + " items" || "0 items",
                product.returnedStockCount + " items" || "0 items",
              ];
              worksheetData.push(productRow);
            });
          } else {
            // No products found
            const productHeaders = [
              "Product Name",
              "Total Stock",
              "Returned Stock",
            ];
            worksheetData.push(productHeaders);
            worksheetData.push(["", "", ""]);
            const emptyProductRow = ["No products found", "", ""];
            worksheetData.push(emptyProductRow);
          }

          // Small delay to prevent overwhelming the server
          await new Promise((resolve) => setTimeout(resolve, 100));
        } catch (productError) {
          console.error(
            `Error fetching products for route ${plan.route}:`,
            productError
          );
          // Add product headers and error row
          const productHeaders = [
            "Product Name",
            "Total Stock",
            "Returned Stock",
          ];
          worksheetData.push(productHeaders);
          worksheetData.push(["", "", ""]);
          const errorProductRow = ["Error fetching products", "", ""];
          worksheetData.push(errorProductRow);
        }

        // Create worksheet for this route plan
        const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);

        // Set column widths
        worksheet["!cols"] = [
          { width: 30 }, // Product Name / Plan Date
          { width: 20 }, // Total Stock / Driver
          { width: 20 }, // Returned Stock / Vehicle
        ];

        // Add styling for hierarchical structure
        const range = XLSX.utils.decode_range(worksheet["!ref"] || "A1");

        // Style header rows
        for (let col = range.s.c; col <= range.e.c; col++) {
          // Style first header row (plan headers)
          const cellRef1 = XLSX.utils.encode_cell({ r: 0, c: col });
          if (worksheet[cellRef1]) {
            worksheet[cellRef1].s = {
              font: { bold: true, color: { rgb: "FFFFFF" } },
              fill: { fgColor: { rgb: "4472C4" } },
              alignment: { horizontal: "center" },
            };
          }
        }

        // Style plan summary row
        for (let col = 0; col <= 2; col++) {
          const cellRef = XLSX.utils.encode_cell({ r: 1, c: col });
          if (worksheet[cellRef]) {
            worksheet[cellRef].s = {
              font: { bold: true },
              fill: { fgColor: { rgb: "E7E6E6" } },
            };
          }
        }

        // Add sheet to workbook with unique name (driver + date)
        const driverName = plan.driver?.name || "Unknown";
        const planDate = plan.planDate
          ? Method.convertDateToFormat(plan.planDate, "DD-MM-YY")
          : processedCount + 1;
        const sheetName = `${driverName} ${planDate}`
          .replace(/[\\/*?:[\]]/g, "_")
          .substring(0, 31);
        XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);

        processedCount++;
        setProcessedRecords(processedCount);
        setProgress((processedCount / total) * 100);

        // Small delay to prevent overwhelming the server
        await new Promise((resolve) => setTimeout(resolve, 200));
      }

      // Create and download Excel file
      setCurrentStep("Generating Excel file...");

      const excelBuffer = XLSX.write(workbook, {
        bookType: "xlsx",
        type: "array",
      });
      const blob = new Blob([excelBuffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `route-product-report-${
        new Date().toISOString().split("T")[0]
      }.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      setCurrentStep("Export completed successfully!");
      setTimeout(() => {
        onHide();
      }, 2000);
    } catch (error) {
      console.error("Export error:", error);
      setError("Export failed: " + (error as Error).message);
    } finally {
      setIsExporting(false);
    }
  };

  const handleClose = () => {
    if (isExporting) {
      if (
        window.confirm(
          "Export is in progress. Are you sure you want to cancel?"
        )
      ) {
        onHide();
      }
    } else {
      onHide();
    }
  };

  return (
    <Modal
      show={show}
      onHide={handleClose}
      backdrop={isExporting ? "static" : true}
      keyboard={!isExporting}
      centered
      size="lg"
    >
      <Modal.Header closeButton={!isExporting}>
        <Modal.Title>Export Route Product Report</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {!isExporting && !error && (
          <div>
            <div className="alert alert-info">
              <h6>
                <strong>Export Information:</strong>
              </h6>
              <ul className="mb-2">
                <li>This will export all route product data to Excel format</li>
                <li>Each route plan will have a separate sheet</li>
                <li>Large datasets may take several minutes to process</li>
                <li>Please keep this window open during export</li>
                <li>The file will automatically download when ready</li>
              </ul>
            </div>

            <div className="alert alert-warning">
              <strong>Note:</strong> For datasets with many routes, the export
              may take a few minutes. Please be patient and do not close this
              window.
            </div>
          </div>
        )}

        {isExporting && (
          <div>
            <div className="d-flex align-items-center mb-3">
              <Spinner animation="border" size="sm" className="me-3" />
              <strong>Exporting Data...</strong>
            </div>

            <div className="mb-3">
              <div className="d-flex justify-content-between mb-2">
                <span>Progress:</span>
                <span>{Math.round(progress)}%</span>
              </div>
              <div className="progress">
                <div
                  className={`progress-bar ${
                    progress === 100 ? "bg-success" : "bg-primary"
                  }`}
                  role="progressbar"
                  style={{ width: `${progress}%` }}
                  aria-valuenow={progress}
                  aria-valuemin={0}
                  aria-valuemax={100}
                ></div>
              </div>
            </div>

            {totalRecords > 0 && (
              <div className="mb-3">
                <small className="text-muted">
                  Processed: {processedRecords.toLocaleString()} of{" "}
                  {totalRecords.toLocaleString()} route plans
                </small>
              </div>
            )}

            <div className="mb-2">
              <small>{currentStep}</small>
            </div>
          </div>
        )}

        {error && (
          <div className="alert alert-danger">
            <strong>Error:</strong> {error}
          </div>
        )}
      </Modal.Body>

      <Modal.Footer>
        {!isExporting && (
          <>
            <Button className="uw-btn uw-btn-light"
              data-analytics-event="finance_reports_route_product_report_cancel_clicked" variant="secondary" onClick={handleClose}>
              Cancel
            </Button>
            <Button className="uw-btn uw-btn-primary"
              data-analytics-event="finance_reports_route_product_report_start_export_clicked" variant="primary" onClick={handleExport}>
              Start Export
            </Button>
          </>
        )}

        {isExporting && (
          <Button className="uw-btn uw-btn-light"
            data-analytics-event="finance_reports_route_product_report_cancel_export_clicked" variant="outline-secondary" onClick={handleClose}>
            Cancel Export
          </Button>
        )}

        {error && (
          <Button className="uw-btn uw-btn-primary"
            data-analytics-event="finance_reports_route_product_report_try_again_clicked"
            variant="primary"
            onClick={() => {
              setError("");
              setProgress(0);
              setCurrentStep("");
            }}
          >
            Try Again
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

const RouteProductReport = () => {
  const { currentUser } = useAuth();
  const { selectedWarehouse } = useWarehouse();
  const navigate = useNavigate();
  const { state }: any = useLocation();
  const [endDate, setEndDate] = useState<any>(new Date());
  const [startDate, setStartDate] = useState<any>(new Date());
  const [fetchLoading, setFetchLoading] = useState(false);
  const [routePlans, setRoutePlans] = useState<any>([]);
  const [download, setDownload] = useState(false);
  const [showPermissionModal, setShowPermissionModal] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [drivers, setDrivers] = useState<any>([]);
  const [selectedDriver, setSelectedDriver] = useState<any>(null);

  // New state for expandable rows
  const [expandedRoute, setExpandedRoute] = useState<any>({});
  const [routeDetails, setRouteDetails] = useState<any>({});
  const [loadingRouteDetails, setLoadingRouteDetails] = useState<any>({});

  useEffect(() => {
    (async () => {
      setFetchLoading(true);
      const warehouseValue = selectedWarehouse?.value || null;
      await fetchRoutePlans(startDate, endDate, warehouseValue || null, null);
      await fetchDrivers(warehouseValue || null);
      setFetchLoading(false);
    })();
  }, [selectedWarehouse]);

  const fetchDrivers = async (warehouseId?: any) => {
    let params: any = {
      pageNo: 1,
      limit: 0,
      sortKey: "name",
      sortOrder: 1,
    };
    if (warehouseId) {
      params.warehouse = warehouseId;
    }
    let apiService = new APICallService(
      driver.listDriver,
      params,
      "",
      "",
      "",
      "",
      RouteProductReports,
      warehouseId
    );
    let response = await apiService.callAPI();
    if (response.records) {
      response.records = response.records.map((val: any) => {
        return {
          label: val.name,
          value: val.name,
          title: val.name,
          _id: val._id,
          image: val.image,
        };
      });
      setDrivers(response.records);
    }
  };

  const fetchRoutePlans = async (
    startDate: string,
    endDate: string,
    warehouse?: any,
    driverId?: any
  ) => {
    let params: any = {
      fromDate: startDate
        ? Method.convertDateToFormat(startDate, "YYYY-MM-DD")
        : "",
      toDate: endDate ? Method.convertDateToFormat(endDate, "YYYY-MM-DD") : "",
      needCount: true,
    };
    if (warehouse) {
      params.warehouse = warehouse;
    }
    if (driverId) {
      params.driver = driverId;
    }
    let apiService = new APICallService(
      routePlanning.listOutForDeliveryPlans,
      params,
      "",
      "",
      "",
      "",
      AllModules,
      warehouse
    );
    let response = await apiService.callAPI();
    if (response && response.records) {
      setRoutePlans(response.records);
      // Reset expanded state when filter changes
      setExpandedRoute({});
      setRouteDetails({});
    } else {
      setRoutePlans([]);
    }
  };

  const fetchRouteDetails = async (routeId: string, warehouse?: any) => {
    setLoadingRouteDetails((prev: any) => ({ ...prev, [routeId]: true }));
    let params: any = {
      fromDate: startDate
        ? Method.convertDateToFormat(startDate, "YYYY-MM-DD")
        : "",
      toDate: endDate ? Method.convertDateToFormat(endDate, "YYYY-MM-DD") : "",
      route: routeId,
      utcOffset: new Date().getTimezoneOffset(),
    };
    if (warehouse) {
      params.warehouse = warehouse;
    }
    let apiService = new APICallService(
      seller.routeProductReport,
      params,
      "",
      "",
      "",
      "",
      AllModules,
      warehouse
    );
    let response = await apiService.callAPI();
    if (response) {
      setRouteDetails((prev: any) => ({
        ...prev,
        [routeId]: response || [],
      }));
    }
    setLoadingRouteDetails((prev: any) => ({ ...prev, [routeId]: false }));
  };

  const handleChange = async ([startDate, endDate]: any) => {
    setStartDate(startDate);
    setEndDate(endDate);
    if (startDate && endDate) {
      setFetchLoading(true);
      await fetchRoutePlans(
        startDate,
        endDate,
        selectedWarehouse?.value || null,
        selectedDriver ? selectedDriver._id : null
      );
      setFetchLoading(false);
    }
  };

  const handleDownload = () => {
    setShowExportModal(true);
  };

  const handleDriverFilter = async (event: any) => {
    if (event) {
      setFetchLoading(true);
      setSelectedDriver(event);
      await fetchRoutePlans(
        startDate,
        endDate,
        selectedWarehouse?.value || null,
        event._id
      );
      setFetchLoading(false);
    } else {
      setFetchLoading(true);
      setSelectedDriver(null);
      await fetchRoutePlans(
        startDate,
        endDate,
        selectedWarehouse?.value || null,
        null
      );
      setFetchLoading(false);
    }
  };

  const toggleRouteDetails = (routeId: string) => {
    setExpandedRoute((prev: any) => ({
      ...prev,
      [routeId]: !prev[routeId],
    }));

    if (!routeDetails[routeId] && !expandedRoute[routeId]) {
      fetchRouteDetails(routeId, selectedWarehouse?.value || null);
    }
  };

  return (
    <>
      {showPermissionModal && (
        <PermissionModal
          show={showPermissionModal}
          onHide={() => setShowPermissionModal(false)}
          moduleName={"orders & delivery"}
        />
      )}
      <ExcelExportModal
        show={showExportModal}
        onHide={() => setShowExportModal(false)}
        startDate={startDate}
        endDate={endDate}
      />
      <div className="p-5">
        <Row className="g-4">
          <Col md>
            <div className="d-flex align-items-center mt-4">
              <h1 className="fs-22 fw-bolder">Delivery Plan Report</h1>
            </div>
          </Col>
          <Col md="auto">
            <div className="d-flex align-items-center">
              <FormLabel className="fs-16 fw-500">Filter by Driver</FormLabel>
              <div className="ms-5">
                <CustomSelect
                  className="form-control bg-white min-h-30px fs-16 fw-bold text-dark min-w-md-288px min-w-175px"
                  onChange={handleDriverFilter}
                  options={drivers}
                  isClearable={true}
                  value={selectedDriver}
                  default={selectedDriver}
                />
              </div>
            </div>
          </Col>
          <Col md="auto">
            <div className="d-flex align-items-center">
              <FormLabel className="fs-16 fw-500">Filter by dates</FormLabel>
              <div className="ms-5">
                <CustomDatePicker
                  className="form-control bg-white min-h-30px fs-16 fw-bold text-dark min-w-md-288px min-w-175px"
                  onChange={handleChange}
                  selectsRange={true}
                  startDate={startDate}
                  endDate={endDate}
                  dateFormat="dd/MM/yyyy"
                  showFullMonthYearPicker={true}
                  maxDate={new Date()}
                  inputTextBG="bg-white"
                />
              </div>
            </div>
          </Col>
          <Col md="auto">
            <Button
              data-analytics-event="finance_reports_route_product_report_download_excel_clicked"
              className="uw-btn uw-btn-primary fs-14 fw-600"
              onClick={handleDownload}
              disabled={fetchLoading}
            >
              <span className="indicator-label fs-16 fw-bold">
                Download Excel
              </span>
            </Button>
          </Col>
        </Row>

        <Card className="border border-r10px mt-6">
          <Card.Body className="p-0">
            <div className="table-responsive">
              <table className="table table-row-bordered datatable align-middle gs-7 gy-4 my-0">
                <thead>
                  <tr className="fw-bold fs-16 fw-600 text-dark border-bottom align-middle">
                    <th className="min-w-md-175px min-w-150px">Plan Date</th>
                    <th className="min-w-md-200px min-w-125px">Driver</th>
                    <th className="min-w-md-200px min-w-125px">Vehicle</th>
                    <th className="min-w-150px text-end"></th>
                  </tr>
                </thead>
                <tbody className="fs-15 fw-600">
                  {fetchLoading ? (
                    <tr>
                      <td colSpan={5}>
                        <div className="d-flex justify-content-center">
                          <Loader loading={fetchLoading} />
                        </div>
                      </td>
                    </tr>
                  ) : (
                    <>
                      {routePlans.length ? (
                        routePlans.map((plan: any, index: number) => (
                          <div key={index} style={{ display: "contents" }}>
                            <tr
                              className={
                                expandedRoute[plan.route] ? "bg-light" : ""
                              }
                              onClick={() => toggleRouteDetails(plan.route)}
                              style={{ cursor: "pointer" }}
                            >
                              <td>
                                <span className="text-dark d-block">
                                  {Method.convertDateToDDMMYYYY(plan.planDate)}
                                </span>
                              </td>
                              <td>
                                <span className="text-dark d-block">
                                  {plan.driver?.name || "N/A"}
                                </span>
                              </td>
                              <td>
                                <span className="text-dark d-block">
                                  {plan.vehicle?.name +
                                    " - " +
                                    plan.vehicle?.number || "N/A"}
                                </span>
                              </td>

                              <td className="text-end">
                                <Button
                                  data-analytics-event="finance_reports_route_product_report_toggle_view_clicked"
                                  variant="link"
                                  className="p-0 me-2 text-muted"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    toggleRouteDetails(plan.route);
                                  }}
                                >
                                  <i
                                    className={`fas fa-chevron-${
                                      expandedRoute[plan.route] ? "up" : "down"
                                    }`}
                                  ></i>
                                </Button>
                              </td>
                            </tr>
                            {expandedRoute[plan.route] && (
                              <tr>
                                <td colSpan={5} className="p-0">
                                  <div className="p-5 bg-light">
                                    {loadingRouteDetails[plan.route] ? (
                                      <div className="d-flex justify-content-center">
                                        <Loader
                                          loading={
                                            loadingRouteDetails[plan.route]
                                          }
                                        />
                                      </div>
                                    ) : (
                                      <div className="table-responsive bg-white rounded p-4">
                                        <table className="table table-row-bordered align-middle gs-7 gy-4 my-0">
                                          <thead>
                                            <tr className="fw-bold fs-14 text-gray-800 border-bottom-2 border-gray-200">
                                              <th className="min-w-150px">
                                                Product Name
                                              </th>
                                              <th className="min-w-150px">
                                                Total Stock
                                              </th>
                                              <th className="min-w-150px">
                                                Returned Stock
                                              </th>
                                              <th className="text-end min-w-100px"></th>
                                            </tr>
                                          </thead>
                                          <tbody>
                                            <>{console.log(routeDetails)}</>
                                            {routeDetails[plan.route] &&
                                            routeDetails[plan.route].length >
                                              0 ? (
                                              routeDetails[plan.route].map(
                                                (
                                                  orderVal: any,
                                                  idx: number
                                                ) => (
                                                  <tr key={idx}>
                                                    <td>{orderVal.title}</td>
                                                    <td>
                                                      {orderVal.stockCount +
                                                        " items"}
                                                    </td>
                                                    <td>
                                                      {orderVal.returnedStockCount +
                                                        " items"}
                                                    </td>
                                                  </tr>
                                                )
                                              )
                                            ) : (
                                              <tr>
                                                <td
                                                  colSpan={3}
                                                  className="text-center text-muted fw-bold"
                                                >
                                                  No product data found for this
                                                  route
                                                </td>
                                              </tr>
                                            )}
                                          </tbody>
                                        </table>
                                      </div>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            )}
                          </div>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={5}>
                            <div className="w-100 fs-15 fw-bold d-flex justify-content-center">
                              No Route Plans found
                            </div>
                          </td>
                        </tr>
                      )}
                    </>
                  )}
                </tbody>
              </table>
            </div>
          </Card.Body>
        </Card>
      </div>
    </>
  );
};
export default RouteProductReport;
